import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import './AuditLogsPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuditLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    action: '',
    user_email: '',
    start_date: '',
    end_date: '',
    limit: 50,
    skip: 0
  });
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchLogs();
    fetchSummary();
  }, [filters]);

  const fetchLogs = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      
      // Check if token exists
      if (!token) {
        setError('Please login to view audit logs');
        window.location.href = '/login';
        return;
      }
      
      const params = new URLSearchParams();
      
      if (filters.action) params.append('action', filters.action);
      if (filters.user_email) params.append('user_email', filters.user_email);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      params.append('limit', filters.limit);
      params.append('skip', filters.skip);
      
      const response = await axios.get(
        `${API_URL}/api/audit-logs?${params.toString()}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to fetch audit logs';
      
      // Handle authentication errors
      if (err.response?.status === 401 || err.response?.status === 403) {
        if (errorMessage.includes('token') || errorMessage.includes('expired')) {
          setError('Your session has expired. Please login again.');
          setTimeout(() => {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/login';
          }, 2000);
        } else {
          setError('You do not have permission to view audit logs. Admin access required.');
        }
      } else {
        setError(errorMessage);
      }
      
      console.error('Error fetching audit logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        return;
      }
      
      const response = await axios.get(
        `${API_URL}/api/audit-logs/summary?days=30`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      setSummary(response.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
      // Don't show error for summary, just log it
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      skip: 0  // Reset to first page when filters change
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (direction) => {
    const newSkip = direction === 'next' 
      ? filters.skip + filters.limit 
      : Math.max(0, filters.skip - filters.limit);
    
    setFilters(prev => ({ ...prev, skip: newSkip }));
    setCurrentPage(direction === 'next' ? currentPage + 1 : currentPage - 1);
  };

  const exportToCSV = () => {
    const csvData = logs.map(log => ({
      Timestamp: log.timestamp,
      Action: log.action,
      User: log.user_email,
      Status: log.status,
      Tenant: log.tenant_id,
      Entity: log.entity_type || '',
      IP: log.ip_address || ''
    }));
    
    const headers = Object.keys(csvData[0]).join(',');
    const rows = csvData.map(row => Object.values(row).join(','));
    const csv = [headers, ...rows].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_logs_${new Date().toISOString()}.csv`;
    a.click();
  };

  const getActionColor = (action) => {
    if (action.includes('failed') || action.includes('denied')) return 'danger';
    if (action.includes('login') || action.includes('create')) return 'success';
    if (action.includes('delete') || action.includes('remove')) return 'warning';
    return 'info';
  };

  const getStatusBadge = (status) => {
    const colors = {
      success: 'badge-success',
      failure: 'badge-danger',
      error: 'badge-danger',
      pending: 'badge-warning'
    };
    return colors[status] || 'badge-secondary';
  };

  return (
    <div className="audit-logs-container">
      <div className="audit-header">
        <h1>Audit Logs</h1>
        <p className="subtitle">Track all system activities and user actions</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="summary-cards">
          <div className="summary-card">
            <h3>{summary.total_actions}</h3>
            <p>Total Actions (30 days)</p>
          </div>
          <div className="summary-card">
            <h3>{summary.unique_users}</h3>
            <p>Unique Users</p>
          </div>
          <div className="summary-card">
            <h3>{summary.actions_breakdown?.length || 0}</h3>
            <p>Action Types</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-row">
          <div className="filter-group">
            <label>Action</label>
            <select 
              name="action" 
              value={filters.action} 
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Actions</option>
              <option value="auth.login">Login</option>
              <option value="auth.failed_login">Failed Login</option>
              <option value="document.batch_upload">Document Upload</option>
              <option value="opinion.generate">Opinion Generated</option>
              <option value="opinion.signature_add">Signature Added</option>
              <option value="opinion.download_docx">DOCX Download</option>
              <option value="opinion.download_pdf">PDF Download</option>
              <option value="user.create">User Created</option>
              <option value="user.role_change">Role Changed</option>
            </select>
          </div>

          <div className="filter-group">
            <label>User Email</label>
            <input
              type="text"
              name="user_email"
              value={filters.user_email}
              onChange={handleFilterChange}
              placeholder="Search by email..."
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              name="start_date"
              value={filters.start_date}
              onChange={handleFilterChange}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              name="end_date"
              value={filters.end_date}
              onChange={handleFilterChange}
              className="filter-input"
            />
          </div>

          <div className="filter-group">
            <button 
              onClick={() => {
                setFilters({
                  action: '',
                  user_email: '',
                  start_date: '',
                  end_date: '',
                  limit: 50,
                  skip: 0
                });
                setCurrentPage(1);
              }}
              className="btn-secondary"
            >
              Clear Filters
            </button>
          </div>
        </div>

        <div className="actions-row">
          <button 
            onClick={exportToCSV} 
            disabled={logs.length === 0}
            className="btn-primary"
          >
            📥 Export to CSV
          </button>
          <button onClick={fetchLogs} className="btn-secondary">
            🔄 Refresh
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading audit logs...</p>
        </div>
      )}

      {/* Audit Logs Table */}
      {!loading && logs.length > 0 && (
        <>
          <div className="table-info">
            <p>Showing {filters.skip + 1} - {Math.min(filters.skip + filters.limit, total)} of {total} logs</p>
          </div>

          <div className="table-container">
            <table className="audit-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Action</th>
                  <th>User</th>
                  <th>Status</th>
                  <th>Entity</th>
                  <th>IP Address</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td className="timestamp">
                      {log.timestamp ? format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm:ss') : 'N/A'}
                    </td>
                    <td>
                      <span className={`action-badge action-${getActionColor(log.action)}`}>
                        {log.action}
                      </span>
                    </td>
                    <td className="user-email">{log.user_email}</td>
                    <td>
                      <span className={`badge ${getStatusBadge(log.status)}`}>
                        {log.status}
                      </span>
                    </td>
                    <td className="entity">
                      {log.entity_type && (
                        <>
                          <span className="entity-type">{log.entity_type}</span>
                          {log.entity_name && (
                            <span className="entity-name">{log.entity_name}</span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="ip">{log.ip_address || '-'}</td>
                    <td className="details">
                      {log.metadata && Object.keys(log.metadata).length > 0 && (
                        <button 
                          className="btn-small"
                          onClick={() => alert(JSON.stringify(log.metadata, null, 2))}
                        >
                          View
                        </button>
                      )}
                      {log.error_message && (
                        <span className="error-indicator" title={log.error_message}>
                          ⚠️
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <button
              onClick={() => handlePageChange('prev')}
              disabled={currentPage === 1}
              className="btn-pagination"
            >
              ← Previous
            </button>
            <span className="page-info">Page {currentPage}</span>
            <button
              onClick={() => handlePageChange('next')}
              disabled={filters.skip + filters.limit >= total}
              className="btn-pagination"
            >
              Next →
            </button>
          </div>
        </>
      )}

      {/* No Data */}
      {!loading && logs.length === 0 && (
        <div className="no-data">
          <p>📋 No audit logs found</p>
          <p className="hint">Try adjusting your filters or check back later</p>
        </div>
      )}
    </div>
  );
};

export default AuditLogsPage;
